export const MARKETING_ATTRIBUTION_STORAGE_KEY = 'ademero.marketingAttribution';
export const MARKETING_ATTRIBUTION_MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

const MARKETING_PARAM_KEYS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id',
  'gclid', 'gbraid', 'wbraid', 'dclid', 'msclkid', 'fbclid',
  'gad_source', 'gad_campaignid', 'am_session_id', 'am_visitor_id', 'am_handoff',
];

const getDefaultStorage = () => {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (err) {
    return null;
  }
};

const getUrl = href => {
  try {
    return new URL(href, typeof window !== 'undefined' ? window.location.origin : undefined);
  } catch (err) {
    return null;
  }
};

const buildMarketingSearch = searchParams => {
  const output = new URLSearchParams();
  MARKETING_PARAM_KEYS.forEach(key => {
    const value = searchParams.get(key);
    if (value) output.set(key, value);
  });
  return output.toString();
};

const sanitizeLandingUrl = url => {
  const query = buildMarketingSearch(url.searchParams);
  return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
};

const sanitizeReferrer = referrer => {
  if (!referrer) return '';
  const url = getUrl(referrer);
  return url ? `${url.origin}${url.pathname}` : '';
};

export const captureMarketingAttribution = ({ href, referrer = '', now = new Date().toISOString() } = {}) => {
  const url = getUrl(href);
  if (!url) return null;

  const params = {};
  MARKETING_PARAM_KEYS.forEach(key => {
    const value = url.searchParams.get(key);
    if (value) params[key] = value;
  });

  return {
    capturedAt: now,
    landingUrl: sanitizeLandingUrl(url),
    referrer: sanitizeReferrer(referrer),
    params,
  };
};

export const readMarketingAttribution = (
  storage = getDefaultStorage(),
  now = Date.now(),
) => {
  if (!storage) return null;
  try {
    const raw = storage.getItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    const attribution = JSON.parse(raw);
    const capturedAt = Date.parse(attribution?.capturedAt || '');
    const invalid = !Number.isFinite(capturedAt)
      || capturedAt > now + (24 * 60 * 60 * 1000)
      || now - capturedAt > MARKETING_ATTRIBUTION_MAX_AGE_MS
      || !attribution.params
      || typeof attribution.params !== 'object';
    if (invalid) {
      storage.removeItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    return attribution;
  } catch (err) {
    return null;
  }
};

export const writeMarketingAttribution = (storage = getDefaultStorage(), attribution) => {
  if (!storage || !attribution) return;
  try {
    storage.setItem(MARKETING_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch (err) {
    // Measurement must never interrupt authentication.
  }
};

export const clearMarketingAttribution = (storage = getDefaultStorage()) => {
  if (!storage) return;
  try {
    storage.removeItem(MARKETING_ATTRIBUTION_STORAGE_KEY);
  } catch (err) {
    // Measurement must never interrupt authentication.
  }
};

export const createMarketingIdentity = prefix => {
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : null;
  if (typeof cryptoApi?.randomUUID === 'function') {
    return `${prefix}_${cryptoApi.randomUUID()}`;
  }
  if (typeof cryptoApi?.getRandomValues === 'function') {
    const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
    return `${prefix}_${Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('')}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}`;
};

export const ensureMarketingIdentity = (
  attribution,
  idFactory = createMarketingIdentity,
) => {
  if (!attribution) return null;
  const params = { ...(attribution.params || {}) };
  if (!params.am_session_id) params.am_session_id = idFactory('ams');
  if (!params.am_visitor_id) params.am_visitor_id = idFactory('amv');
  return { ...attribution, params };
};

export const captureOrReadMarketingAttribution = ({
  href = typeof window !== 'undefined' ? window.location.href : '',
  referrer = typeof document !== 'undefined' ? document.referrer || '' : '',
  storage = getDefaultStorage(),
  now,
  idFactory = createMarketingIdentity,
} = {}) => {
  const captured = captureMarketingAttribution({ href, referrer, now });
  if (captured && Object.keys(captured.params).length) {
    const identified = ensureMarketingIdentity(captured, idFactory);
    writeMarketingAttribution(storage, identified);
    return identified;
  }
  const stored = readMarketingAttribution(storage);
  if (stored) {
    const identified = ensureMarketingIdentity(stored, idFactory);
    writeMarketingAttribution(storage, identified);
    return identified;
  }
  const identified = ensureMarketingIdentity(captured, idFactory);
  if (identified) writeMarketingAttribution(storage, identified);
  return identified;
};

export const clearSessionStoragePreservingAttribution = (storage = getDefaultStorage(), preserveKeys = []) => {
  if (!storage) return;
  const attribution = readMarketingAttribution(storage);
  const preserved = [];
  try {
    preserveKeys.forEach(key => {
      const value = storage.getItem(key);
      if (value !== null) preserved.push([key, value]);
    });
    storage.clear();
    preserved.forEach(([key, value]) => storage.setItem(key, value));
  } catch (err) {
    return;
  }
  writeMarketingAttribution(storage, attribution);
};
